import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { BookOpen, User } from "lucide-react";
import ShareButton from "@/components/ShareButton";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
}

export default function ChatMessage({ role, content }: ChatMessageProps) {
  const isUser = role === "user";

  return (
    <div className={cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row")}>
      <div
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-full",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-secondary text-secondary-foreground"
        )}
      >
        {isUser ? <User size={20} /> : <BookOpen size={20} />}
      </div>
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-5 py-4 text-lg leading-relaxed",
          isUser
            ? "rounded-br-md bg-primary text-primary-foreground"
            : "rounded-bl-md bg-primary/10 text-card-foreground shadow-sm border border-primary/20"
        )}
      >
        {isUser ? (
          <p>{content}</p>
        ) : (
          <>
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-2.5 prose-p:leading-relaxed prose-ul:my-2 prose-li:my-1 prose-blockquote:my-3 prose-blockquote:border-primary/40 prose-blockquote:text-muted-foreground prose-a:text-primary prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-primary/80">
              <ReactMarkdown
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium"
                    >
                      {children}
                    </a>
                  ),
                  hr: () => (
                    <hr className="!my-8 border-border/40" />
                  ),
                  h3: ({ children }) => (
                    <h3 className="!mt-10 !mb-2">{children}</h3>
                  ),
                  h2: ({ children }) => (
                    <h2 className="!mt-10 !mb-2">{children}</h2>
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
            <ShareButton content={content} />
          </>
        )}
      </div>
    </div>
  );
}
